if (!customElements.get('mobile-collection-loader')) {
  customElements.define('mobile-collection-loader', class extends HTMLElement {
    connectedCallback() {
      this.grid = this.closest('#ProductGridContainer')?.querySelector('ul#product-grid');
      if (!this.grid || !('IntersectionObserver' in window)) return;
      this.collection = this.parentElement;
      this.nextUrl = this.dataset.nextUrl || '';
      this.page = Number(this.dataset.page) || 1;
      this.status = this.querySelector('[role="status"]');
      this.retry = this.querySelector('button');
      this.fallback = this.querySelector('a');
      this.onRetry = () => { this.failed = false; this.loadPage(); };
      this.onLeave = this.savePosition.bind(this);
      this.hasScrolled = window.scrollY > 0;
      this.onFirstScroll = () => {
        this.hasScrolled = true;
        if (this.nextUrl && !this.failed && !this.loading && !this.restoring) this.observer.observe(this);
      };
      this.onLink = event => {
        if (event.target.closest('a[href*="/products/"]')) this.savePosition();
      };
      this.retry.addEventListener('click', this.onRetry);
      this.collection.addEventListener('click', this.onLink);
      window.addEventListener('pagehide', this.onLeave);
      window.addEventListener('scroll', this.onFirstScroll, { once: true, passive: true });
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !this.restoring && !this.failed) this.loadPage();
      }, { rootMargin: '0px 0px 240px 0px' });
      this.collection.classList.add('cc-scroll-pagination-active');
      this.classList.add('is-active');
      if (this.hasScrolled && this.nextUrl) this.observer.observe(this);
      this.onVisibility = () => {
        if (!document.hidden && this.hasScrolled && this.nextUrl && !this.failed && !this.loading && !this.restoring) {
          this.observer.unobserve(this);
          this.observer.observe(this);
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.restorePosition();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.retry?.removeEventListener('click', this.onRetry);
      this.collection?.removeEventListener('click', this.onLink);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pagehide', this.onLeave);
      window.removeEventListener('scroll', this.onFirstScroll);
    }

    setStatus(message) {
      this.status.textContent = message;
    }

    requestUrl() {
      const next = new URL(this.nextUrl, window.location.href);
      if (next.origin !== location.origin || next.pathname.replace(/\/+$/, '') !== location.pathname.replace(/\/+$/, '')) throw new Error('Invalid collection page');
      const current = new URL(location.href);
      for (const key of new Set(current.searchParams.keys())) {
        if (!['page', 'section_id', 'sections'].includes(key) && !next.searchParams.has(key)) {
          current.searchParams.getAll(key).forEach(value => next.searchParams.append(key, value));
        }
      }
      next.searchParams.set('section_id', this.dataset.sectionId);
      return next;
    }

    async loadPage() {
      if (!this.isConnected || !this.nextUrl || this.loading || document.hidden) return false;
      this.loading = true;
      this.observer.disconnect();
      this.retry.hidden = true;
      this.fallback.hidden = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.setStatus(this.dataset.loadingMessage);
      const controller = this.controller = new AbortController();
      try {
        const url = this.requestUrl();
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = parsed.querySelector('ul#product-grid');
        const nextLoader = parsed.querySelector('mobile-collection-loader');
        const nextPage = Number(nextLoader?.dataset.page);
        if (!nextGrid || nextGrid.dataset.id !== this.grid.dataset.id || !nextLoader || nextPage !== this.page + 1) throw new Error('Invalid collection response');
        if (controller.signal.aborted || !this.isConnected) return false;
        const existingIds = new Set([...this.grid.querySelectorAll(':scope > [data-cc-product-id]')].map(item => item.dataset.ccProductId));
        const fragment = document.createDocumentFragment();
        let count = 0;
        for (const item of [...nextGrid.children]) {
          if (!item.matches('li[data-cc-product-id]') || existingIds.has(item.dataset.ccProductId)) continue;
          existingIds.add(item.dataset.ccProductId);
          item.dataset.ccScrollPage = String(nextPage);
          item.querySelectorAll('script:not([type="application/json"]):not([type="application/ld+json"])').forEach(script => script.remove());
          item.querySelectorAll('img').forEach(image => {
            image.loading = 'lazy';
            image.removeAttribute('fetchpriority');
          });
          item.querySelectorAll('.scroll-trigger').forEach(element => element.classList.add('scroll-trigger--cancel'));
          if (item.classList.contains('scroll-trigger')) item.classList.add('scroll-trigger--cancel');
          fragment.append(item);
          count += 1;
        }
        this.grid.append(fragment);
        this.page = nextPage;
        this.nextUrl = nextLoader.dataset.nextUrl || '';
        this.failed = false;
        document.dispatchEvent(new CustomEvent('cc:collection-updated'));
        this.setStatus(this.nextUrl ? this.dataset.loadedMessage.replace('[count]', String(count)) : this.dataset.completeMessage);
        return true;
      } catch (error) {
        if (!controller.signal.aborted && this.isConnected) {
          this.failed = true;
          this.setStatus(this.dataset.errorMessage);
          this.retry.hidden = false;
          this.fallback.href = this.nextUrl;
          this.fallback.hidden = false;
        }
        return false;
      } finally {
        if (this.controller === controller) {
          this.loading = false;
          this.grid.removeAttribute('aria-busy');
          if (this.isConnected && this.hasScrolled && this.nextUrl && !this.failed && !this.restoring) this.observer.observe(this);
        }
      }
    }

    savePosition() {
      if (!this.isConnected) return;
      try {
        history.replaceState({ ...history.state, searchParams: history.state?.searchParams ?? location.search.slice(1), ccCollectionScroll: { key: location.pathname + location.search, page: this.page, y: scrollY } }, '', location.href);
      } catch (_) {}
    }

    async restorePosition() {
      const saved = history.state?.ccCollectionScroll;
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation?.type !== 'back_forward' || saved?.key !== location.pathname + location.search || saved.page <= this.page) return;
      this.restoring = true;
      this.observer.disconnect();
      while (this.isConnected && this.page < saved.page && this.nextUrl) {
        if (!await this.loadPage()) break;
      }
      this.restoring = false;
      if (!this.isConnected) return;
      if (this.page >= saved.page) {
        await document.fonts.ready;
        requestAnimationFrame(() => { if (this.isConnected) window.scrollTo({ top: saved.y, behavior: 'instant' }); });
      }
      if (this.hasScrolled && this.nextUrl && !this.failed) this.observer.observe(this);
    }
  });
}
